import { useCallback, useMemo } from "react"

import { parseISO } from "date-fns"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { Campaign } from "@l2r-front/l2r-proptypes"
import { I18N_NAMESPACE, useCampaigns } from "@l2r-front/l2r-shared-campaign"

import { useCampaignEdit } from "../../hooks/mutations/useCampaignEdit"

import { CampaignForm } from "../CampaignForm"

const MAX_CAMPAIGNS_FOR_NAME_VALIDATION = 1000

export interface CampaignEditFormProps {
    campaign: Campaign
    onClose: (_checkModifications?: boolean) => void
    onChange: (hasChanges: boolean) => void
    onSubmitting: (_submitting: boolean) => void
}

export const CampaignEditForm = (props: CampaignEditFormProps) => {

    const {
        campaign,
        onClose,
        onChange,
        onSubmitting,
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)

    const campaignMutation = useCampaignEdit(campaign.uuid, {}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "forms.campaignEditForm.snackbar.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "forms.campaignEditForm.snackbar.success"),
        onSuccess: () => {
            onSubmitting(false)
            onClose(false)
        },
        onError: () => {
            onSubmitting(false)
        },
    })
    const { data: campaignsData, isLoading: isLoadingCampaigns } = useCampaigns({ limit: MAX_CAMPAIGNS_FOR_NAME_VALIDATION })

    const otherCampaigns = useMemo(() => {
        return (campaignsData?.results || []).filter(otherCampaign => otherCampaign.uuid !== campaign.uuid)
    }, [campaignsData, campaign.uuid])

    const defaultValues = useMemo(() => ({
        campaign_type: campaign.campaign_type,
        color: campaign.color,
        comment: campaign.comment,
        company_name: campaign.company_name,
        date_end: campaign.date_end ? parseISO(campaign.date_end) : null,
        date_start: parseISO(campaign.date_start),
        name: campaign.name,
        tags: campaign.tags || [],
    }), [campaign])

    const onSubmit = useCallback((formValues) => {
        campaignMutation.mutate(formValues)
        onSubmitting(true)
    }, [campaignMutation, onSubmitting])

    if (isLoadingCampaigns) {
        return null
    }

    return <CampaignForm
        campaigns={otherCampaigns}
        defaultValues={defaultValues}
        isSubmitting={campaignMutation.isPending}
        onSubmit={onSubmit}
        onChange={onChange}
        onCancel={() => onClose()}
    />
}
